<template>
  <div v-if="hasMessage">
    <template v-if="groupByType && translatedMutationErrors.length == 0">
      <div
        v-if="translatedFeedbackGrouped.errors.length"
        class="mutation-feedback"
        :class="{ alert: true }"
        role="alert"
        data-test="feedbackErrors"
      >
        <div
          v-if="!ignoreIcon"
          class="icon-column"
          style="align-items: baseline"
          data-test="iconCol"
        >
          <slot name="icon"
            ><v-icon :large="$vuetify.display.mdAndUp" color="red"
              >$iconBell</v-icon
            ></slot
          >
        </div>
        <div class="feedbacks">
          <div v-if="$slots['feedback-error-title']" class="feedback-title">
            <slot name="feedback-error-title"></slot>
          </div>
          <template v-if="translatedFeedbackGrouped.errors.length == 1">
            <div v-html="translatedFeedbackGrouped.errors[0]"></div>
          </template>
          <ul v-else>
            <li
              v-for="(
                responseMessage, index
              ) in translatedFeedbackGrouped.errors"
              :key="index + '-errors'"
              v-html="responseMessage"
            />
          </ul>
        </div>
      </div>
      <br
        v-if="
          translatedFeedbackGrouped.warnings.length ||
          translatedFeedbackGrouped.infos.length
        "
      />

      <div
        v-if="translatedFeedbackGrouped.warnings.length"
        class="mutation-feedback"
        :class="{
          alert: true
        }"
        role="alert"
        data-test="feedbackWarnings"
      >
        <div
          v-if="!ignoreIcon"
          class="icon-column"
          style="align-items: baseline"
          data-test="iconCol"
        >
          <slot name="icon"
            ><v-icon :large="$vuetify.display.mdAndUp" color="red"
              >$iconBell</v-icon
            ></slot
          >
        </div>
        <div class="feedbacks">
          <div v-if="$slots['feedback-warning-title']" class="feedback-title">
            <slot name="feedback-warning-title"></slot>
          </div>
          <template v-if="translatedFeedbackGrouped.warnings.length == 1">
            <div v-html="translatedFeedbackGrouped.warnings[0]"></div>
          </template>
          <ul v-else>
            <li
              v-for="(
                responseMessage, index
              ) in translatedFeedbackGrouped.warnings"
              :key="index + '-warning'"
              v-html="responseMessage"
            ></li>
          </ul>
        </div>
      </div>

      <br v-if="translatedFeedbackGrouped.infos.length" />

      <div
        v-if="translatedFeedbackGrouped.infos.length"
        class="mutation-feedback"
        :class="{ 'primary-light': true }"
        role="alert"
        data-test="feedbackInfos"
      >
        <div
          v-if="!ignoreIcon"
          class="icon-column"
          style="align-items: baseline"
          data-test="iconCol"
        >
          <slot name="icon"
            ><v-icon :large="$vuetify.display.mdAndUp" color="primary"
              >$iconClipboard</v-icon
            ></slot
          >
        </div>

        <div class="feedbacks">
          <div v-if="$slots['feedback-info-title']" class="feedback-title">
            <slot name="feedback-info-title"></slot>
          </div>
          <template v-if="translatedFeedbackGrouped.infos.length == 1">
            <div v-html="translatedFeedbackGrouped.infos[0]"></div>
          </template>
          <ul v-else>
            <li
              v-for="(
                responseMessage, index
              ) in translatedFeedbackGrouped.infos"
              :key="index + '-infos'"
              v-html="responseMessage"
            ></li>
          </ul>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        class="mutation-feedback"
        :class="{ [feedbackStatus.color]: true }"
        role="alert"
      >
        <div
          v-if="!ignoreIcon"
          class="icon-column"
          style="align-items: baseline"
          data-test="iconCol"
        >
          <slot name="icon"
            ><v-icon
              :large="$vuetify.display.mdAndUp"
              :color="feedbackStatus.iconColor"
              >{{ feedbackStatus.icon }}</v-icon
            ></slot
          >
        </div>
        <div class="feedbacks">
          <div
            v-if="translatedMutationErrors.length"
            data-test="mutationErrors"
          >
            <div class="feedback-title">
              <slot name="feedback-mutation-error-title"></slot>
            </div>
            <template v-if="translatedMutationErrors.length == 1">
              <div v-html="translatedMutationErrors[0]"></div>
            </template>
            <ul v-else>
              <li
                v-for="(responseMessage, index) in translatedMutationErrors"
                :key="index + '-mutation-errors'"
                v-html="responseMessage"
              ></li>
            </ul>
          </div>

          <template v-else>
            <div v-if="$slots['feedback-title']" class="feedback-title">
              <slot name="feedback-title"></slot>
            </div>
            <template v-if="translatedFeedback.length == 1">
              <div
                data-test="feedbackMessages"
                v-html="translatedFeedback[0]"
              ></div>
            </template>
            <ul v-else data-test="feedbackMessages">
              <li
                v-for="(responseMessage, index) in translatedFeedback"
                :key="index"
                v-html="responseMessage"
              ></li>
            </ul>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import { defineComponent, computed, toRefs, ref } from "vue";
import gql from "fraql";
import {
  useDisplayText,
  DisplayTextFragments
} from "@composables/display_text";

export default defineComponent({
  name: "AppShowMutationFeedback",
  props: {
    feedbackMessages: { type: Array, default: () => [] },
    mutationErrors: { type: Array, default: () => [] },
    groupByType: { type: Boolean, default: false },
    ignoreIcon: { type: Boolean, default: false }
  },
  setup(props) {
    // These are set trough setManually. Method itself gets called py parent component
    const anyFeedbackMessages = ref([]);
    const anyMutationErrors = ref([]);

    const { feedbackMessages: fm, mutationErrors: mr } = toRefs(props);

    const feedbackMessages = computed(() => {
      return anyFeedbackMessages.value.concat(fm.value);
    });

    const mutationErrors = computed(() => {
      return anyMutationErrors.value.concat(mr.value);
    });

    function setManually({
      manualFeedbackMessages = [],
      manualMutationErrors = []
    }) {
      anyFeedbackMessages.value = manualFeedbackMessages ?? [];
      anyMutationErrors.value = manualMutationErrors ?? [];
    }

    function clearManually() {
      anyFeedbackMessages.value = [];
      anyMutationErrors.value = [];
    }

    const { displayText, translateFeedbackMessages, translateGraphQlErrors } =
      useDisplayText();

    const translatedFeedback = computed(() =>
      translateFeedbackMessages(feedbackMessages.value ?? [])
    );

    const translatedFeedbackGrouped = computed(() => {
      const messages = feedbackMessages.value ?? [];
      return messages.reduce(
        (obj, message) => {
          const translated = displayText(message);
          switch (message.messageType) {
            case "WARNING":
              obj.warnings.push(translated);
              break;
            case "INFO":
              obj.infos.push(translated);
              break;
            default:
              obj.errors.push(translated);
          }

          return obj;
        },
        { errors: [], warnings: [], infos: [] }
      );
    });

    const translatedMutationErrors = computed(() =>
      translateGraphQlErrors(mutationErrors.value ?? [])
    );

    const hasMessage = computed(() => {
      return (
        feedbackMessages.value.length > 0 || mutationErrors.value.length > 0
      );
    });

    const feedbackStatus = computed(() => {
      if (
        translatedFeedbackGrouped.value.errors.length ||
        mutationErrors.value.length
      ) {
        return { color: "bg-alert", icon: "$iconBell", iconColor: "red" };
      } else if (translatedFeedbackGrouped.value.warnings.length) {
        return {
          color: "alert",
          icon: "$iconBell",
          iconColor: "red"
        };
      }

      return {
        color: "bg-primary-light",
        icon: "$iconClipboard",
        iconColor: "primary"
      };
    });

    return {
      feedbackStatus,
      hasMessage,
      translatedFeedbackGrouped,
      translatedFeedback,
      translatedMutationErrors,
      // These are only used by parent component
      setManually,
      clearManually
    };
  },
  fragments: {
    translationFields: gql`
      fragment _ on DisplayText {
        ${DisplayTextFragments.translation}
      }
    `
  }
});
</script>
<style scoped lang="scss">
.mutation-feedback {
  display: flex;
  flex-direction: row;

  background-color: rgb(var(--v-theme-alert));
  padding: 16px;

  .feedback-title {
    font-size: 18px;
    margin-bottom: 4px;
    font-weight: bold;

    @media (max-width: 576px) {
      font-size: 16px;
    }
  }

  @media (max-width: 576px) {
    flex-direction: column;
  }

  .icon-column {
    width: auto;
    padding: 0 12px;

    display: flex;
    justify-content: center;
    @media (max-width: 576px) {
      width: 100%;
      display: block;
      padding: 0;
      text-align: center;
      margin-right: 0px;
      padding-bottom: 9px;
    }
  }

  .feedbacks {
    max-width: 90%;
    padding: 0 12px;

    ul {
      li {
        margin-bottom: 4px;
      }
    }

    @media (max-width: 576px) {
      max-width: 100%;
      display: block;
      padding: 0;
      text-align: center;
      font-size: 12px;
    }
  }
}
</style>
